import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { BarChart3, Eye, MousePointerClick, TrendingUp, Calendar, Loader2 } from 'lucide-react';

export default function Analytics() {
  const [dateRange, setDateRange] = useState('7d');

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();

    switch (dateRange) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      default:
        start.setDate(start.getDate() - 7);
    }

    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    };
  };

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', dateRange],
    queryFn: () => api.getAnalytics(getDateRange())
  });

  const analyticsData = Array.isArray(analytics) ? analytics : [];

  const totals = analyticsData.reduce(
    (acc, day: any) => ({
      views: acc.views + (day.total_views || 0),
      uniqueViews: acc.uniqueViews + (day.total_unique_views || 0),
      ctaClicks: acc.ctaClicks + (day.total_cta_clicks || 0)
    }),
    { views: 0, uniqueViews: 0, ctaClicks: 0 }
  );

  const stats = [
    {
      name: 'Total Views',
      value: totals.views,
      icon: Eye,
      color: 'bg-blue-500'
    },
    {
      name: 'Unique Views',
      value: totals.uniqueViews,
      icon: TrendingUp,
      color: 'bg-green-500'
    },
    {
      name: 'CTA Clicks',
      value: totals.ctaClicks,
      icon: MousePointerClick,
      color: 'bg-purple-500'
    },
    {
      name: 'Click Rate',
      value: totals.views > 0 ? `${((totals.ctaClicks / totals.views) * 100).toFixed(1)}%` : '0%',
      icon: BarChart3,
      color: 'bg-orange-500'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500">Track your answer node performance</p>
        </div>
        <div className="flex items-center space-x-2">
          <Calendar className="h-5 w-5 text-gray-400" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input w-auto"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.name} className="card flex items-center space-x-4">
            <div className={`${stat.color} p-3 rounded-lg`}>
              <stat.icon className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{stat.name}</p>
              <p className="text-2xl font-bold text-gray-900">
                {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Daily Chart */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Performance</h2>
        {analyticsData.length > 0 ? (
          <div className="space-y-4">
            {/* Simple bar chart representation */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Views</th>
                    <th className="pb-3 font-medium">Unique</th>
                    <th className="pb-3 font-medium">CTA Clicks</th>
                    <th className="pb-3 font-medium">Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {analyticsData.map((day: any) => {
                    const maxViews = Math.max(...analyticsData.map((d: any) => d.total_views || 0));
                    const percentage = maxViews > 0 ? ((day.total_views || 0) / maxViews) * 100 : 0;

                    return (
                      <tr key={day.date} className="hover:bg-gray-50">
                        <td className="py-3 text-sm text-gray-900">{day.date}</td>
                        <td className="py-3 text-sm text-gray-900">{(day.total_views || 0).toLocaleString()}</td>
                        <td className="py-3 text-sm text-gray-900">{(day.total_unique_views || 0).toLocaleString()}</td>
                        <td className="py-3 text-sm text-gray-900">{(day.total_cta_clicks || 0).toLocaleString()}</td>
                        <td className="py-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No analytics data yet</h3>
            <p className="text-gray-500">
              Analytics will appear here once your published answers start receiving traffic
            </p>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="card bg-blue-50 border border-blue-100">
        <div className="flex items-start space-x-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <TrendingUp className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Track AI Citations</h3>
            <p className="text-sm text-blue-700">
              Analytics also tracks when your answer content is cited by AI search engines like
              Google AI Overviews, ChatGPT, and Perplexity. This data helps measure your content's
              visibility in AI-powered search results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
